import { termConfig, termMarketsByChain } from '@1delta/data-sdk'
import { TermPriceConsumerAbi } from '@1delta/abis'
import {
  FetcherResult,
  MulticallCall,
  OraclePriceEntry,
  ParseContext,
} from '../types'
import { createMarketUid } from '../../../utils'
import { termLenderKey } from '../../../lending/public-data/term/convertPublic'

/**
 * Term Finance oracle price fetcher.
 *
 * Term values collateral against `TermPriceConsumerV3` (one singleton per
 * chain, `config.priceOracle`) — the SAME oracle it liquidates against, so it
 * is the correct source for a Term borrower's liquidation risk (its prices can
 * differ from a generic Chainlink read; without this, Term collateral was only
 * priced by cross-protocol flattening, which uses OTHER protocols' feeds).
 *
 * `usdValueOfTokens(token, amount)` returns the USD value of `amount` tokens as
 * an Exp `{ mantissa }` (1e18-scaled). Passing `amount = 10^decimals` yields the
 * price of ONE whole token → direct USD (PASS 1). Many repos share the same
 * collateral/loan tokens, so calls are DEDUPED per unique token (one call each)
 * and the parser fans each priced token out to every repo that uses it.
 */

interface TermTokenMeta {
  token: string
  /** All repo lender keys (`TERM_FINANCE_<id>`) that reference this token. */
  repoKeys: string[]
}

/** One `usdValueOfTokens(token, 10^dec)` call per unique Term token on a chain. */
export function getTermCalls(chainId: string): FetcherResult<TermTokenMeta>[] {
  const oracle = termConfig()?.[chainId]?.priceOracle
  const markets = termMarketsByChain(chainId)
  if (!oracle || markets.length === 0) return []

  // token(lower) → { amount (10^decimals), repoKeys[] }
  const byToken = new Map<string, { amount: bigint; repoKeys: Set<string> }>()
  const add = (token: string, decimals: number, repoKey: string) => {
    const t = token.toLowerCase()
    let e = byToken.get(t)
    if (!e) {
      e = { amount: 10n ** BigInt(decimals), repoKeys: new Set() }
      byToken.set(t, e)
    }
    e.repoKeys.add(repoKey)
  }

  for (const m of markets) {
    if (!m.termRepoId) continue
    const repoKey = termLenderKey(m.termRepoId)
    // Loan token (purchase token) + every collateral leg.
    if (m.purchaseToken) add(m.purchaseToken, m.loanDecimals ?? 18, repoKey)
    for (const c of m.collateralParams ?? []) {
      if (c.token) add(c.token, c.decimals ?? 18, repoKey)
    }
  }

  const results: FetcherResult<TermTokenMeta>[] = []
  for (const [token, { amount, repoKeys }] of byToken) {
    const calls: MulticallCall[] = [
      { address: oracle, name: 'usdValueOfTokens', params: [token, amount] },
    ]
    results.push({
      calls,
      meta: { token, repoKeys: [...repoKeys] },
      lender: 'TERM_FINANCE',
    })
  }
  return results
}

/**
 * Parse a token's `usdValueOfTokens(token, 10^dec)` result (Exp mantissa, the
 * per-whole-token USD price in 1e18) into one direct-USD entry per repo that
 * uses the token, keyed by that repo's `TERM_FINANCE_<id>` lender so the
 * marketUids join the Term lending data.
 */
export function parseTermResults(
  data: any[],
  meta: TermTokenMeta,
  context: ParseContext,
): OraclePriceEntry[] {
  const { chainId } = context
  const raw = data?.[0]
  if (raw == null || raw === '0x') return []
  // Exp tuple decodes to `{ mantissa }` (object) or `[mantissa]` (array).
  const mantissa =
    typeof raw === 'bigint'
      ? raw
      : Array.isArray(raw)
        ? BigInt(raw[0] ?? 0)
        : raw?.mantissa != null
          ? BigInt(raw.mantissa)
          : 0n
  const priceUSD = Number(mantissa) / 1e18
  if (!(priceUSD > 0)) return []

  const entries: OraclePriceEntry[] = []
  for (const repoKey of meta.repoKeys) {
    entries.push({
      asset: meta.token,
      price: priceUSD,
      priceUSD,
      marketUid: createMarketUid(chainId, repoKey, meta.token),
      targetLender: repoKey,
      description: 'Term price oracle (USD)',
    })
  }
  return entries
}

/** ABI for the Term oracle call (`usdValueOfTokens`). */
export function getTermAbi() {
  return TermPriceConsumerAbi
}

/** Term Finance Oracle Fetcher module. */
export const termFetcher = {
  getCalls: getTermCalls,
  parse: parseTermResults,
  getAbi: getTermAbi,
}
